import { InvalidCatalogDefinitionError, UnknownMemberError } from "./errors.model";

// ============================================================
// Member identity — system-assigned, never author-typed
//
// Every structural member of a definition (observation, component, parameter,
// choice option, section, item, requirement, step) carries an identity the
// system assigns. Authors supply LABELS only; they never type an identifier.
//
// THE RULE BEING SERVED: a removed member's identity is never reused, because
// recorded values cite it. With author-typed keys nothing stops someone
// deleting `systolic` and re-adding a different observation under the same key
// a year later, silently inheriting the old readings — so the system has to
// permanently remember every key ever deleted, per lineage, at every nesting
// level. That is the seven retired-key registries, the NUL-joined composite
// keys and the `validateRetiredIdentities` walk in
// `diagnostic-procedure-definition.model.ts`, roughly 150 lines, and the same
// again in `exam-definition.model.ts`.
//
// Assigned ids make collision impossible BY CONSTRUCTION. There is therefore
// NO retired-key registry in the new models, and none should ever be added —
// if one appears, something has started letting authors choose identities
// again, and that is the bug to fix.
//
// Cost, accepted: raw stored data shows opaque identities rather than readable
// keys, so debugging needs a lookup. Fixtures pin their identities as literals.
// ============================================================

declare const memberIdBrand: unique symbol;

/** Named so other packages' declarations can name a MemberId without the private symbol. */
export interface MemberIdMarker {
  readonly [memberIdBrand]: true;
}

/**
 * A system-assigned member identity. Branded so a label, a FHIR code or a raw
 * form value cannot be passed where an identity is expected — the whole point
 * is that these are never typed by hand, and an unbranded `string` would let
 * exactly that through the type checker.
 */
export type MemberId = string & MemberIdMarker;

/** Anything the system assigns an identity to. `id` is absent while the member is new. */
export interface Member {
  id?: MemberId;
}

const MEMBER_ID = /^([a-z][a-z0-9]*)-([1-9]\d*)$/;

/**
 * Narrow a stored string to a `MemberId`. The one sanctioned crossing, for
 * mappers rehydrating persisted state and for fixtures pinning literals.
 * Everything inside the domain gets its ids from a sequence.
 */
export function asMemberId(value: string): MemberId {
  if (!MEMBER_ID.test(value)) {
    throw new InvalidCatalogDefinitionError(`'${value}' is not a member identity`, { memberId: value });
  }
  return value as MemberId;
}

export function isMemberId(value: unknown): value is MemberId {
  return typeof value === "string" && MEMBER_ID.test(value);
}

/**
 * Mints identities for one collection within one lineage.
 *
 * `high` is a HIGH-WATER MARK, not a count, and it is aggregate state that
 * must be persisted alongside the members. Seeding it from the live members
 * would reuse the id of a removed member the moment the removed one was the
 * highest — the exact failure assigned ids exist to prevent. `highWaterMark`
 * below exists only for rehydrating state written before the mark was stored,
 * and it carries that hazard; a lineage that has never removed a member is the
 * only case where it is safe.
 */
export interface MemberIdSequence {
  next(): MemberId;
  /** The highest number issued so far. Persist this; it is what makes reuse impossible across loads. */
  readonly high: number;
}

/**
 * A sequence over `<prefix>-<n>`. Deterministic on purpose: the domain does no
 * I/O and reaches for no randomness, so a test asserting `obs-3` is stable and
 * a fixture can pin its identities as literals.
 */
export function memberIdSequence(prefix: string, high = 0): MemberIdSequence {
  if (!/^[a-z][a-z0-9]*$/.test(prefix)) {
    throw new InvalidCatalogDefinitionError(`Member id prefix '${prefix}' must be lower-case alphanumeric`);
  }
  if (!Number.isInteger(high) || high < 0) {
    throw new InvalidCatalogDefinitionError(`Member id high-water mark must be a non-negative integer, got ${high}`);
  }
  let issued = high;
  return {
    next(): MemberId {
      issued += 1;
      return `${prefix}-${issued}` as MemberId;
    },
    get high() {
      return issued;
    },
  };
}

/**
 * The highest number among ids already present. A REHYDRATION FALLBACK for
 * lineages persisted without their mark — see the hazard on `MemberIdSequence`.
 * Prefer the stored mark whenever there is one.
 */
export function highWaterMark(members: readonly Member[]): number {
  let high = 0;
  for (const member of members) {
    const match = member.id ? MEMBER_ID.exec(member.id) : null;
    if (match) high = Math.max(high, Number(match[2]));
  }
  return high;
}

/**
 * Carry identities across a setter.
 *
 * Setters take the whole collection, so without this a setter would treat its
 * argument as fresh content and remint every id — silently breaking every
 * stored reference to a member, such as a protocol occurrence citing a
 * `parameterId`. Three rules, from the model behaviour doc:
 *
 * - a member WITH A KNOWN id is that member, edited;
 * - a member WITH NO id is new, and gets one;
 * - a member with an id the aggregate DOES NOT RECOGNISE is an error, not an
 *   invitation to create one.
 *
 * `known` is the ids currently on the aggregate. A removed member's id is not
 * in it, so re-sending one is refused — which is the same protection the
 * retired-key registries were built for, obtained for free.
 */
export function assignMemberIds<T extends Member>(
  incoming: readonly T[],
  known: ReadonlySet<MemberId>,
  sequence: MemberIdSequence,
  collection: string,
): T[] {
  const seen = new Set<MemberId>();
  return incoming.map((member) => {
    if (member.id === undefined) return { ...member, id: sequence.next() };
    if (!known.has(member.id)) {
      throw new UnknownMemberError(`No ${collection} member with identity '${member.id}'`, {
        memberId: member.id,
        collection,
      });
    }
    if (seen.has(member.id)) {
      throw new InvalidCatalogDefinitionError(`${collection} member '${member.id}' appears twice`, {
        memberId: member.id,
        collection,
      });
    }
    seen.add(member.id);
    return { ...member };
  });
}

/** The ids of a collection, in the shape `assignMemberIds` wants for `known`. */
export function memberIds(members: readonly Member[]): Set<MemberId> {
  const ids = new Set<MemberId>();
  for (const member of members) {
    if (member.id !== undefined) ids.add(member.id);
  }
  return ids;
}
